import { useState, useMemo, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type UserDto,
  type UserListQuery,
  type UserRole,
  type UserStatus,
  USER_ROLES,
  USER_STATUSES,
} from '@ctms/contracts';
import {
  AlertCircle,
  Ban,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  KeyRound,
  RotateCcw,
  Search,
  Shield,
  ShieldAlert,
  UserCheck,
  UserPlus,
} from 'lucide-react';

import { usersApi } from '../users.api.js';
import { authApi } from '../../auth/auth.api.js';
import { userQueryKeys } from '../users.query-keys.js';
import { CreateUserDialog } from '../create-user-dialog/create-user-dialog.js';
import { getApiErrorMessage } from '../../../api/client.js';
import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';

const columnHelper = createAppColumnHelper<UserDto>();

export function UserTable(): JSX.Element {
  const queryClient = useQueryClient();

  // Search & filter state
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<UserStatus | undefined>(undefined);

  // Dialog and feedback state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const queryFilters: UserListQuery = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() ? search.trim() : undefined,
      role: roleFilter,
      status: statusFilter,
    }),
    [page, limit, search, roleFilter, statusFilter],
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: userQueryKeys.list(queryFilters),
    queryFn: () => usersApi.listUsers(queryFilters),
    staleTime: 30_000,
  });

  // Toggle finance access mutation
  const toggleFinanceMutation = useMutation({
    mutationFn: async ({ id, hasFinanceAccess }: { id: number; hasFinanceAccess: boolean }) => {
      return usersApi.updateUser(id, { hasFinanceAccess });
    },
    onSuccess: (updatedUser) => {
      setActionError(null);
      setActionSuccess(
        `Zaktualizowano uprawnienia finansowe dla pracownika ${updatedUser.displayName}.`,
      );
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
    onError: async (err: unknown) => {
      const msg = await getApiErrorMessage(err, 'Nie udało się zmienić uprawnień finansowych.');
      setActionError(msg);
    },
  });

  // Change status (suspend/activate) mutation
  const changeStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: UserStatus }) => {
      return usersApi.updateUser(id, { status });
    },
    onSuccess: (updatedUser) => {
      setActionError(null);
      const isSuspended = updatedUser.status === USER_STATUSES.SUSPENDED;
      setActionSuccess(
        isSuspended
          ? `Konto pracownika ${updatedUser.displayName} zostało zablokowane. Wszystkie aktywne sesje unieważniono.`
          : `Konto pracownika ${updatedUser.displayName} zostało odblokowane.`,
      );
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
    onError: async (err: unknown) => {
      const msg = await getApiErrorMessage(err, 'Nie udało się zmienić statusu konta.');
      setActionError(msg);
    },
  });

  // Reset 2FA mutation
  const resetMfaMutation = useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      return authApi.resetMfa(id);
    },
    onSuccess: () => {
      setActionError(null);
      setActionSuccess('Zabezpieczenie 2FA zostało zresetowane. Pracownik skonfiguruje nowe 2FA przy kolejnym logowaniu.');
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
    onError: async (err: unknown) => {
      const msg = await getApiErrorMessage(err, 'Nie udało się zresetować zabezpieczenia 2FA.');
      setActionError(msg);
    },
  });

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('displayName', {
          header: 'Pracownik',
          cell: (info) => {
            const user = info.row.original;
            return (
              <div>
                <div className="font-medium text-slate-100">{user.displayName}</div>
                <div className="text-xs text-slate-400 font-mono">{user.email}</div>
              </div>
            );
          },
        }),
        columnHelper.accessor('role', {
          header: 'Rola w ośrodku',
          cell: (info) => {
            const role = info.getValue();
            const isAdmin = role === USER_ROLES.TENANT_ADMIN;
            return (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  isAdmin
                    ? 'border border-amber-800/80 bg-amber-950/60 text-amber-300'
                    : 'border border-slate-700 bg-slate-800/70 text-slate-300'
                }`}
              >
                <Shield className="h-3 w-3" />
                {isAdmin ? 'Administrator Ośrodka' : 'Zwykły Użytkownik'}
              </span>
            );
          },
        }),
        columnHelper.accessor('hasFinanceAccess', {
          header: 'Moduł finansowy',
          cell: (info) => {
            const user = info.row.original;
            const hasAccess = user.hasFinanceAccess;
            const isAdmin = user.role === USER_ROLES.TENANT_ADMIN;

            return (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={hasAccess || isAdmin}
                  disabled={isAdmin || toggleFinanceMutation.isPending}
                  onClick={() =>
                    toggleFinanceMutation.mutate({
                      id: user.id,
                      hasFinanceAccess: !hasAccess,
                    })
                  }
                  title={
                    isAdmin
                      ? 'Administrator posiada stały dostęp do finansów'
                      : hasAccess
                        ? 'Kliknij, aby odebrać dostęp do finansów'
                        : 'Kliknij, aby nadać dostęp do finansów'
                  }
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:cursor-not-allowed disabled:opacity-60 ${
                    hasAccess || isAdmin ? 'bg-emerald-600' : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      hasAccess || isAdmin ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-xs text-slate-300">
                  {isAdmin ? (
                    <span className="text-amber-300 font-medium">Stały (Admin)</span>
                  ) : hasAccess ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400">
                      <DollarSign className="h-3 w-3" /> Aktywny
                    </span>
                  ) : (
                    <span className="text-slate-500">Zablokowany</span>
                  )}
                </span>
              </div>
            );
          },
        }),
        columnHelper.accessor('status', {
          header: 'Status konta',
          cell: (info) => {
            const status = info.getValue();
            if (status === USER_STATUSES.ACTIVE) {
              return (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-800/80 bg-emerald-950/60 px-2 py-0.5 text-xs text-emerald-300">
                  <UserCheck className="h-3 w-3" /> Aktywne
                </span>
              );
            }
            if (status === USER_STATUSES.SUSPENDED) {
              return (
                <span className="inline-flex items-center gap-1 rounded-full border border-rose-800/80 bg-rose-950/60 px-2 py-0.5 text-xs text-rose-300">
                  <Ban className="h-3 w-3" /> Zablokowane
                </span>
              );
            }
            return (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-800 px-2 py-0.5 text-xs text-slate-400">
                Nieaktywne
              </span>
            );
          },
        }),
        columnHelper.accessor('twoFactorEnabled', {
          header: '2FA (TOTP)',
          cell: (info) => {
            const enabled = info.getValue();
            return enabled ? (
              <span className="inline-flex items-center gap-1 text-xs text-cyan-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> Skonfigurowane
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                <ShieldAlert className="h-3.5 w-3.5" /> Nieskonfigurowane
              </span>
            );
          },
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Akcje administracyjne',
          cell: (info) => {
            const user = info.row.original;
            const isSuspended = user.status === USER_STATUSES.SUSPENDED;

            return (
              <div className="flex items-center gap-2">
                {/* Suspend / activate button */}
                <button
                  type="button"
                  onClick={() =>
                    changeStatusMutation.mutate({
                      id: user.id,
                      status: isSuspended ? USER_STATUSES.ACTIVE : USER_STATUSES.SUSPENDED,
                    })
                  }
                  disabled={changeStatusMutation.isPending}
                  title={isSuspended ? 'Odblokuj konto pracownika' : 'Zablokuj konto pracownika'}
                  className={`inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs font-medium transition-colors ${
                    isSuspended
                      ? 'border-emerald-700 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/60'
                      : 'border-rose-800/70 bg-rose-950/30 text-rose-300 hover:bg-rose-900/60'
                  }`}
                >
                  {isSuspended ? (
                    <>
                      <Check className="h-3 w-3" /> Odblokuj
                    </>
                  ) : (
                    <>
                      <Ban className="h-3 w-3" /> Zablokuj
                    </>
                  )}
                </button>

                {/* Reset 2FA button */}
                {user.twoFactorEnabled && (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Czy na pewno zresetować 2FA dla pracownika ${user.displayName}? Przy kolejnym logowaniu wymagana będzie ponowna konfiguracja.`,
                        )
                      ) {
                        resetMfaMutation.mutate({ id: user.id });
                      }
                    }}
                    disabled={resetMfaMutation.isPending}
                    title="Awaryjny reset zabezpieczenia 2FA"
                    className="inline-flex items-center gap-1 rounded border border-cyan-800/60 bg-cyan-950/30 px-2 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60 transition-colors"
                  >
                    <KeyRound className="h-3 w-3" /> Reset 2FA
                  </button>
                )}
              </div>
            );
          },
        }),
      ]),
    [toggleFinanceMutation, changeStatusMutation, resetMfaMutation],
  );

  const table = useAppTable({
    data: data?.items ?? [],
    columns,
  });

  return (
    <div className="space-y-4">
      {/* Notifications / Alerts */}
      {actionSuccess && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded-md border border-emerald-800/70 bg-emerald-950/40 p-3 text-xs text-emerald-300"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
            <span>{actionSuccess}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionSuccess(null)}
            className="text-emerald-400 hover:text-emerald-200"
          >
            Zamknij
          </button>
        </div>
      )}

      {actionError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 rounded-md border border-rose-800/70 bg-rose-950/40 p-3 text-xs text-rose-300"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
            <span>{actionError}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="text-rose-400 hover:text-rose-200"
          >
            Zamknij
          </button>
        </div>
      )}

      {/* Toolbar: Search + Filters + Add button */}
      <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-900/80 p-3">
        <div className="flex items-center gap-3 flex-1">
          {/* Search bar */}
          <div className="relative max-w-sm flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
              <Search className="h-3.5 w-3.5" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Szukaj po nazwisku lub e-mailu…"
              className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pr-3 pl-8 text-xs text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {/* Role filter */}
          <select
            value={roleFilter ?? ''}
            onChange={(e) => {
              setRoleFilter(
                e.target.value ? (e.target.value as UserRole) : undefined,
              );
              setPage(1);
            }}
            aria-label="Filtruj wg roli"
            className="rounded-md border border-slate-700 bg-slate-950 py-1.5 px-2.5 text-xs text-slate-300 focus:border-cyan-500 focus:outline-none"
          >
            <option value="">Wszystkie role</option>
            <option value={USER_ROLES.USER}>Zwykły Użytkownik</option>
            <option value={USER_ROLES.TENANT_ADMIN}>Administrator Ośrodka</option>
          </select>

          {/* Status filter */}
          <select
            value={statusFilter ?? ''}
            onChange={(e) => {
              setStatusFilter(
                e.target.value ? (e.target.value as UserStatus) : undefined,
              );
              setPage(1);
            }}
            aria-label="Filtruj wg statusu"
            className="rounded-md border border-slate-700 bg-slate-950 py-1.5 px-2.5 text-xs text-slate-300 focus:border-cyan-500 focus:outline-none"
          >
            <option value="">Wszystkie statusy</option>
            <option value={USER_STATUSES.ACTIVE}>Aktywne</option>
            <option value={USER_STATUSES.SUSPENDED}>Zablokowane</option>
            <option value={USER_STATUSES.INACTIVE}>Nieaktywne</option>
          </select>

          <button
            type="button"
            onClick={() => void refetch()}
            title="Odśwież listę personelu"
            className="rounded-md border border-slate-700 bg-slate-800 p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Add user action */}
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 transition-colors shadow-xs"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Dodaj pracownika
        </button>
      </div>

      {/* TanStack Table */}
      <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/60 shadow-xs">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3 px-4 text-xs font-medium tracking-wider text-slate-400 uppercase"
                  >
                    <table.FlexRender header={header} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-slate-800/80">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-slate-400">
                  Ładowanie ewidencji personelu…
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-rose-400">
                  Nie udało się załadować listy personelu. Sprawdź połączenie z serwerem.
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-slate-500">
                  Brak pracowników spełniających kryteria wyszukiwania.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="transition-colors hover:bg-slate-800/40 focus-within:bg-slate-800/40"
                >
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} className="py-3 px-4">
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex items-center justify-between text-xs text-slate-400 px-1">
        <div>
          {data ? (
            <span>
              Łącznie: <strong className="text-slate-200">{data.total}</strong> pracowników
              {data.total > 0 && (
                <>
                  {' '}
                  (strona <strong className="text-slate-200">{data.page}</strong> z{' '}
                  <strong className="text-slate-200">
                    {Math.max(1, Math.ceil(data.total / data.limit))}
                  </strong>
                  )
                </>
              )}
            </span>
          ) : (
            <span>Pobieranie danych…</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1 || isLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex items-center gap-1 rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Poprzednia
          </button>
          <button
            type="button"
            disabled={!data?.hasNext || isLoading}
            onClick={() => setPage((p) => p + 1)}
            className="flex items-center gap-1 rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Następna <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Create User Modal Dialog */}
      <CreateUserDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={() => {
          setActionSuccess('Konto pracownika zostało pomyślnie utworzone.');
          void queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
        }}
      />
    </div>
  );
}
